// App.tsx - the Postflop SPA shell. Hosts the phone frame, the stage pill, and the tap-to-learn
// glossary; routes the active surface. S20 builds Observe, S21 Diagnose, S24 Solo, S25 the Progress
// surface. S26 adds the Local/Pro context menu (the real nav chrome) and the import-review surface,
// reached by quiet affordances beside the stage pill; deep-links ?mode=menu and ?mode=import.
// GH4 boots the app on the Splash/home surface (pick a game → sit down); the wordmark returns home.
//
// TB2 - the continuous table lives HERE. The session (roster, carried stacks, button, hand number) is App
// state, created once in `sitDown()`. It has to be at this level: the live surfaces are remounted on every
// New-hand and wiped on every stage switch, so any table held inside one lasts exactly one hand. That was
// the bug - "next hand" dealt a brand-new table of strangers. Now `newHand()` advances the SAME session
// and the surfaces render whatever hand it hands them.
import { useRef, useState } from 'react';
import { AppShell } from './components/AppShell';
import { StagePill, type Stage } from './components/StagePill';
import { GlossaryProvider } from './components/Glossary';
import { ObserveView } from './observe/ObserveView';
import { DiagnoseView } from './diagnose/DiagnoseView';
import { SoloStage } from './solo/SoloStage';
import { LiveObserve } from './live/LiveObserve';
import { LiveDiagnose } from './live/LiveDiagnose';
import { LiveSolo } from './live/LiveSolo';
import { ProgressView } from './progress/ProgressView';
import { ContextMenu } from './replay/ContextMenu';
import { ImportReview } from './replay/ImportReview';
import { Splash } from './home/Splash';
import { TournamentSession } from './tournament/TournamentSession';
import type { TournamentPresetId } from './engine/tournamentRunner';
import {
  openTable,
  nextHand,
  endHand,
  playDecision,
  freshSeedRoot,
  tagSeat,
  confirmSeatReads,
  seatTags,
  withReads,
  DEFAULT_TABLE_SIZE,
  type TableSize,
  type TableState,
} from './live/table';
import { TAG_ARCHETYPE, type PlayerTag } from './diagnose/data';
import type { TableMode } from './home/tableMode';
import {
  STAKES_POOLS,
  DEFAULT_STAKES_ID,
  DEFAULT_BUY_IN_BB,
  buyInFor,
  stakesCrumb,
  type StakesId,
} from './home/stakes';
import { loadRoll, resetRoll, saveRoll, type Roll } from './bankroll/bankroll';
import type { ActionKind, ArchetypeId } from '@postflop/schema';

/** The top-level surface. 'splash' is the boot/home screen; 'play' shows the Observe/Diagnose/Solo spine. */
type Surface = 'splash' | 'play' | 'progress' | 'menu' | 'import';

function initialMode(): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('mode');
}
function initialStage(): Stage {
  const m = initialMode();
  return m === 'diagnose' || m === 'solo' ? m : 'observe';
}
/** The DEFAULT is live/random generated hands (GH3). ?hand=lesson opens the authored guided lesson instead. */
function initialLive(): boolean {
  if (typeof window === 'undefined') return true;
  return new URLSearchParams(window.location.search).get('hand') !== 'lesson';
}
/** Boot on the Splash unless a deep-link asks for a specific surface/hand (keeps ?mode= and ?seed= working
 *  for screenshots + shares). A bare load (no params) lands on the splash - the app no longer drops
 *  straight into a hand. */
function initialSurface(): Surface {
  const m = initialMode();
  if (m === 'progress') return 'progress';
  if (m === 'menu') return 'menu';
  if (m === 'import') return 'import';
  if (m === 'observe' || m === 'diagnose' || m === 'solo' || m === 'play') return 'play';
  if (typeof window !== 'undefined') {
    const p = new URLSearchParams(window.location.search);
    if (p.get('seed') || p.get('hand')) return 'play'; // deterministic/lesson deep-links skip the splash.
  }
  return 'splash';
}

/** `?seed=` pins the whole SESSION (TB2), not one hand: same root ⇒ same opponents, same hands, same order,
 *  forever. That is what makes the deep-link useful for screenshots + shares now that the table persists. */
function seedRootParam(): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('seed');
}

/**
 * Every session hand is dealt with the played preflop ON.
 *
 * There is ONE hand per hand number, shared by all three lenses - that is the point of TB2 ("all three
 * surfaces render the session's current hand"), and it is what makes a stage switch keep the hand you are
 * in instead of silently dealing a new one. So the model has to carry hero's real preflop decision, which
 * Diagnose/Solo play (PF4) and Observe simply does not read: `LiveObserve` narrates its own GH8 recap off
 * `terminal.history`, which is present either way. The visible change to Observe is that hero's preflop is
 * now his REAL chart line rather than the open/call scaffolding autopilot - which is the Phase 10 scope
 * correction (full hands, preflop → river), not a regression.
 */
const SESSION_DEAL = { preflopPlay: true };

export function App() {
  const [stage, setStage] = useState<Stage>(initialStage);
  const [surface, setSurface] = useState<Surface>(initialSurface);
  const [live, setLive] = useState<boolean>(initialLive);
  // ST-UI: the game type you sat down at. 'cash' is the continuous TableSession spine; 'tournament' is the
  // ICM push/fold spot surface (TournamentPlay). Like stakes/size it is a property of the game you sit down
  // at - chosen on the splash, fixed for the sit-down - so it lives here, above the surfaces. The nonce is
  // the tournament "next hand": bumping it re-deals a fresh spot (the surface is keyed on it).
  const [gameType, setGameType] = useState<'cash' | 'tournament'>('cash');
  const [tournamentPreset, setTournamentPreset] = useState<TournamentPresetId>('local_100');
  const [tournamentNonce, setTournamentNonce] = useState(0);
  // TB3: the size of the table you sit down at (TB0 §9 F1). Chosen on the splash, consumed by `sitDown` -
  // it can only change BETWEEN sessions, never mid-session, because re-sizing a table you are sitting at
  // would mean seating or standing up players, which is exactly the busts/rebuys/leave/join TB0 §5 defers.
  const [size, setSize] = useState<TableSize>(DEFAULT_TABLE_SIZE);
  // CX5: the stakes you sit down at + how deep you buy in. Like `size`, these are chosen on the splash and
  // can only change BETWEEN sessions (a new sit-down) - the stakes and everyone's buy-in are fixed for the
  // life of a continuous table, so they live here, above the surfaces, and feed `openTable` in `sitDown`.
  const [stakesId, setStakesId] = useState<StakesId>(DEFAULT_STAKES_ID);
  const [buyInDepthBB, setBuyInDepthBB] = useState<number>(DEFAULT_BUY_IN_BB);
  // CX6: the Solo-only bankroll. `soloChoice` is the sit-down decision - null (not yet chosen -> Solo shows
  // the gate), 'roll' (playing for the persisted bankroll), or 'fresh' (casual, roll untouched). It is reset
  // per sit-down, because "sit with your roll vs sit fresh" is a fresh choice every time you sit. `roll` is
  // the persisted play-money roll for the CURRENT stakes, loaded at sit-down and moved only by Solo-bankroll
  // hands. Both live here, above the surfaces, for the same reason the table does - a surface is remounted
  // and wiped every hand, and the choice + the roll must outlive that.
  const [soloChoice, setSoloChoice] = useState<'roll' | 'fresh' | null>(null);
  const [roll, setRoll] = useState<Roll>(() => loadRoll(DEFAULT_STAKES_ID));
  // How much each hand has already contributed to the roll, keyed `sessionId:handNumber` (globally unique, so
  // hand 1 of a new sit never collides with hand 1 of the last). It makes the settle REVISABLE: replaying a
  // hand from the report (a different result) re-settles the DELTA instead of double-counting, and it never
  // double-counts a hand that merely re-rendered. Survives remounts as a ref.
  const settledRef = useRef<Map<string, number>>(new Map());
  // TB2: the continuous table. Null until you sit down; a deep-link into the play spine opens one on boot
  // so ?mode=diagnose / ?seed= keep working. It is state at THIS level on purpose - see the header note.
  const [table, setTable] = useState<TableState | null>(() =>
    initialSurface() === 'play' && initialLive()
      ? openTable({ mode: 'mid', seedRoot: seedRootParam() ?? freshSeedRoot() }, SESSION_DEAL)
      : null,
  );

  const pickStage = (s: Stage) => {
    setStage(s);
    setSurface('play');
  };
  const goHome = () => {
    setSurface('splash');
    window.scrollTo({ top: 0 });
  };
  /**
   * Next hand at the SAME table (TB2). This is the line that used to throw the table away: it bumped a
   * nonce, React remounted the surface, `useLiveDeal` ran again, and you got six new strangers with fresh
   * 150bb stacks. Now it advances the session - stacks carried, button rotated one seat, same seats.
   */
  const newHand = () => {
    if (!table) return;
    // Dealt HERE, in the handler - not inside a setState updater. An updater runs during React's render
    // phase and StrictMode deliberately double-invokes it, which would deal every hand twice. nextHand is
    // pure so the result would still be correct, but a session hand costs synchronous engine work and
    // paying that twice is not something to leave lying around.
    //
    // FULL: HARVEST FIRST, then deal. The hand you just played is banked into the session here - this is
    // the moment your chips become the table's truth, and it is why the stacks that carry are now yours
    // rather than a check-call bot's. `endHand` is pure; it returns the advanced session.
    const { session } = endHand(table);
    setTable(nextHand(session, session.tableMode, SESSION_DEAL));
    window.scrollTo({ top: 0 });
  };
  /**
   * FULL - you really played a decision. Rebuild the hand around it.
   *
   * Lives at App level for the same reason the table does: the hand has to survive a lens switch, and a
   * surface is remounted (and wiped) the moment you move. `playDecision` is pure - it replays the hand from
   * its dealt state with your action recorded, so the tail you have not reached yet re-derives from what you
   * actually did instead of from what a bot would have done.
   */
  const playHeroDecision = (stepIndex: number, action: ActionKind, amount?: string) => {
    setTable((t) => (t ? playDecision(t, stepIndex, action, amount, t.session.tableMode) : t));
  };
  /**
   * TB3 - you locked a read on a seat. Write it to the SESSION, which is the only place it can survive:
   * the surface that captured it is remounted (and wiped) on the very next hand. Because the read is on the
   * session, hand 9 still shows the note you made on seat 4 on hand 3 - the compounding-reads payoff the
   * Full-Hand Poker amendment is built on.
   *
   * The current hand's felts are re-painted immediately (`withReads` is a pure re-map, not a re-deal), so
   * the tag lands on the pod the moment you lock it rather than showing up a hand late.
   */
  const lockSeatRead = (seat: number, tag: PlayerTag) => {
    const archetypeId = TAG_ARCHETYPE[tag];
    if (!archetypeId) return; // "Not sure yet" is an honest non-read - it writes nothing.
    setTable((t) => {
      if (!t) return t;
      const session = tagSeat(t.session, seat, archetypeId);
      if (session === t.session) return t; // idempotent re-lock; don't churn the model.
      return { ...t, session, model: withReads(t.model, session) };
    });
  };
  /**
   * TB4 - a showdown proved a seat's archetype in a pot you FOLDED out of. Write it to the session.
   *
   * Same reason `lockSeatRead` lives up here: the surface that saw the showdown is remounted and wiped on
   * the next hand, and the entire value of a folded-hand read is that it OUTLIVES the hand it came from.
   * Joe: "lots of information from folded hands at the table" - this is the line that stops that
   * information evaporating the moment the pot is pushed.
   *
   * Distinct from `lockSeatRead`, which writes `userTags` (what YOU think). This writes `confirmedTag`
   * (what the table SHOWED) and never touches your tag - being proven wrong does not silently rewrite your
   * note, it just means the beat tells you to re-tag him.
   */
  const confirmReads = (evidence: readonly { seat: number; archetypeId: ArchetypeId }[]) => {
    setTable((t) => {
      if (!t) return t;
      const session = confirmSeatReads(t.session, evidence);
      return session === t.session ? t : { ...t, session };
    });
  };
  /**
   * CX6 - settle the CURRENT Solo hand into the roll. Called by LiveSolo when a hand reaches its report (and
   * again if a revise changes the result). App is the authority: it reads the hand's realized net straight
   * off its own table (`model.heroNetChips` - won pot positive, lost/folded negative, a busted-and-reloaded
   * hand nets to the real loss), rounds it, and applies it to the persisted roll.
   *
   * REVISABLE + idempotent by design: `settledRef` remembers what this hand already contributed, so a
   * re-settle applies only the delta (never a double count), and `handsPlayed` ticks up only the first time.
   * A no-op unless the player is sitting WITH the roll - which is how Observe/Diagnose (and a fresh sit) never
   * move it.
   */
  const settleSoloHand = () => {
    if (!table || soloChoice !== 'roll') return;
    const key = `${table.session.sessionId}:${table.handNumber}`;
    const net = Math.round(table.model.heroNetChips);
    const prior = settledRef.current.get(key);
    if (prior === net) return; // already settled at this result; nothing to move.
    settledRef.current.set(key, net);
    setRoll((cur) => {
      const firstSettle = prior === undefined;
      const next: Roll = {
        ...cur,
        balance: cur.balance - (prior ?? 0) + net,
        handsPlayed: firstSettle ? cur.handsPlayed + 1 : cur.handsPlayed,
        updatedAt: Date.now(),
      };
      saveRoll(stakesId, next);
      return next;
    });
  };
  /** CX6 - reset the current stakes' roll to its starting balance (a busted/short roll, or a fresh start). */
  const resetSoloRoll = () => setRoll(resetRoll(stakesId));

  /** Sit down from the splash: OPEN A TABLE (New table = cold read / Mid-session = diagnosed) and deal its
   *  first hand, landing on the Observe lens of the spine. The one and only place a session is created. */
  const sitDown = (mode: TableMode) => {
    // CX5: the chosen stakes pool (blinds + villain mix + table-type) and the buy-in depth become the session.
    // `stackDepthBB` follows from buyIn/bb inside `specForHand`, so the depth you picked flows all the way to
    // the deal; the pool roster's archetypes carry their own range families + read difficulty (the real
    // $1/$3-vs-$3/$5 toughness, not a number swap).
    const pool = STAKES_POOLS[stakesId];
    setTable(
      openTable(
        {
          mode,
          seedRoot: seedRootParam() ?? freshSeedRoot(),
          tableSize: size,
          stakes: pool.stakes,
          tableType: pool.tableType,
          roster: pool.roster,
          buyIn: buyInFor(stakesId, buyInDepthBB),
        },
        SESSION_DEAL,
      ),
    );
    // CX6: a NEW sit-down = a fresh bankroll choice, and the roll for the stakes you just chose. The roll
    // number carries (loaded from storage); the "sit with roll vs fresh" decision does not - Solo asks it
    // again, because it is a per-sit-down choice.
    setSoloChoice(null);
    setRoll(loadRoll(stakesId));
    setGameType('cash');
    setStage('observe');
    setLive(true);
    setSurface('play');
    window.scrollTo({ top: 0 });
  };

  /** ST-UI: sit down at a local tournament. Game type is a session property (like stakes/size), so this is
   *  the tournament analogue of `sitDown` - it sets the game type + preset and lands on the Observe lens.
   *  The tournament spine has no continuous TableSession (a spot is a single ICM push/fold decision), so it
   *  routes to <TournamentPlay> instead of the Live cash surfaces. */
  const sitDownTournament = (buyIn: '$100' | '$200') => {
    setTournamentPreset(buyIn === '$200' ? 'local_200' : 'local_100');
    setTournamentNonce(0);
    setGameType('tournament');
    setStage('observe');
    setSurface('play');
    window.scrollTo({ top: 0 });
  };
  const newTournamentHand = () => {
    setTournamentNonce((n) => n + 1);
    window.scrollTo({ top: 0 });
  };

  if (surface === 'splash') {
    return (
      <GlossaryProvider>
        <div className="phone">
          <Splash
            onSit={sitDown}
            onSitTournament={sitDownTournament}
            size={size}
            onSize={setSize}
            stakesId={stakesId}
            onStakes={setStakesId}
            buyInDepthBB={buyInDepthBB}
            onBuyInDepth={setBuyInDepthBB}
            onImport={() => setSurface('import')}
            onProgress={() => setSurface('progress')}
          />
        </div>
      </GlossaryProvider>
    );
  }

  // the top-bar "New hand" control only makes sense on the live play spine (not Progress/Menu/Import, and
  // not the guided lesson hand, which is a single authored hand with no re-deal).
  const canNewHand = surface === 'play' && live && table !== null;
  // ST-UI: on the tournament spine "New hand" deals a fresh spot (bumps the nonce); on the cash spine it
  // advances the continuous session. Both feed the top bar's one control.
  const isTournament = gameType === 'tournament';
  const topNewHand = surface === 'play' ? (isTournament ? newTournamentHand : canNewHand ? newHand : undefined) : undefined;
  // Remount the surface once per HAND, so per-hand UI state (which street you are on, the read lock, the
  // accumulated grades) resets when the next hand is dealt. This is the same `key` trick GH9 used, but
  // keyed on the session's real hand number instead of a blind nonce - the table itself lives above it in
  // App state and is untouched by the remount, which is exactly the state that used to get wiped.
  const handKey = table ? `${table.session.sessionId}:${table.handNumber}` : 'none';

  return (
    <GlossaryProvider>
      <AppShell
        stakesLabel={isTournament ? `Local . ${tournamentPreset === 'local_200' ? '$200' : '$100'} MTT` : stakesCrumb(stakesId)}
        onHome={goHome}
        onNewHand={topNewHand}
      >
        <div className="navrow">
          <StagePill stage={stage} onChange={pickStage} />
          {/* ST-UI: the read-only Cash|Tournament indicator - which game you sat down at. Game type is a
              session property (chosen on the splash), so this shows it rather than toggling it. */}
          {surface === 'play' && (
            <span className="gtpill" role="status" aria-label="Game type">
              <span className={isTournament ? '' : 'on'}>Cash</span>
              <span className={isTournament ? 'on' : ''}>Tourney</span>
            </span>
          )}
          {surface === 'play' && !isTournament && (
            <button
              type="button"
              className={'livetoggle' + (live ? ' on' : '')}
              aria-pressed={live}
              onClick={() => setLive((v) => !v)}
              title={live ? 'Playing live generated hands' : 'Playing the guided lesson hand'}
            >
              {live ? 'Live' : 'Lesson'}
            </button>
          )}
          <button
            type="button"
            className={'progbtn' + (surface === 'progress' ? ' on' : '')}
            aria-pressed={surface === 'progress'}
            onClick={() => setSurface('progress')}
          >
            Progress
          </button>
          <button
            type="button"
            className={'progbtn' + (surface === 'menu' || surface === 'import' ? ' on' : '')}
            aria-pressed={surface === 'menu' || surface === 'import'}
            onClick={() => setSurface('menu')}
          >
            Menu
          </button>
        </div>
        {surface === 'progress' ? (
          <ProgressView />
        ) : surface === 'menu' ? (
          <ContextMenu
            mode="local"
            onLocal={() => setSurface('play')}
            onImport={() => setSurface('import')}
          />
        ) : surface === 'import' ? (
          <ImportReview />
        ) : isTournament ? (
          // TF1: the CONTINUOUS tournament spine - a running multi-table field (field-sim), live first-in
          // jam-or-fold via PF5, named save/resume, and a $0 narrated hand. Keyed on the nonce so the
          // top-bar "New hand" starts a fresh run.
          <TournamentSession
            key={`tourney-${tournamentPreset}-${tournamentNonce}`}
            presetId={tournamentPreset}
            stage={stage}
            runNonce={tournamentNonce}
          />
        ) : stage === 'observe' ? (
          live && table ? <LiveObserve key={handKey} model={table.model} onNextHand={newHand} /> : <ObserveView />
        ) : stage === 'diagnose' ? (
          live && table ? (
            <LiveDiagnose
              key={handKey}
              model={table.model}
              onNextHand={newHand}
              onLockRead={lockSeatRead}
              seatTags={seatTags(table.session)}
              onConfirmReads={confirmReads}
              onHeroDecision={playHeroDecision}
            />
          ) : (
            <DiagnoseView />
          )
        ) : live && table ? (
          <LiveSolo
            key={handKey}
            model={table.model}
            onNextHand={newHand}
            onLockRead={lockSeatRead}
            seatTags={seatTags(table.session)}
            onConfirmReads={confirmReads}
            onHeroDecision={playHeroDecision}
            bankroll={{
              choice: soloChoice,
              onChoose: setSoloChoice,
              roll,
              buyIn: buyInFor(stakesId, buyInDepthBB),
              stakesLabel: STAKES_POOLS[stakesId].label,
              onSettleHand: settleSoloHand,
              onResetRoll: resetSoloRoll,
            }}
          />
        ) : (
          <SoloStage />
        )}
      </AppShell>
    </GlossaryProvider>
  );
}
